<script>
  // Owns the whole photo picker: the drop target, the thumbnail previews and
  // the file input the form actually submits. Files accumulate across picks and
  // drops, which a file input cannot do on its own, so every change to the
  // selection is written back to the input through a DataTransfer.

  // name is the input's form field name, e.g. "entity[photos][]".
  let { name } = $props()

  const ACCEPT = "image/png,image/jpg,image/jpeg,image/gif,image/webp"

  let input = $state()
  let dragging = $state(false)
  let nextId = 0
  let picked = $state([])

  function add(files) {
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue
      picked.push({ id: nextId++, file, preview: URL.createObjectURL(file) })
    }
  }

  function remove(id) {
    const index = picked.findIndex((entry) => entry.id === id)
    if (index === -1) return

    URL.revokeObjectURL(picked[index].preview)
    picked.splice(index, 1)
  }

  function handleChange(event) {
    add(event.target.files)
  }

  function handleDrop(event) {
    event.preventDefault()
    dragging = false
    add(event.dataTransfer.files)
  }

  function shortName(fileName) {
    if (fileName.length <= 20) return fileName
    return `${fileName.slice(0, 15)}...${fileName.split(".").pop()}`
  }

  $effect(() => {
    const transfer = new DataTransfer()
    for (const entry of picked) transfer.items.add(entry.file)
    input.files = transfer.files
  })

  $effect(() => () => {
    for (const entry of picked) URL.revokeObjectURL(entry.preview)
  })
</script>

<!-- Drag and drop is a pointer-only shortcut; the button inside carries the
     keyboard and assistive-technology path, so the box itself has no role. -->
<div
  class="dropzone"
  class:is-dragging={dragging}
  role="presentation"
  ondragover={(event) => {
    event.preventDefault()
    dragging = true
  }}
  ondragleave={() => (dragging = false)}
  ondrop={handleDrop}
>
  <button type="button" class="dropzone-open" onclick={() => input.click()}>
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
    <span>Drop images here or tap to select</span>
    <span class="is-size-7 has-text-grey">PNG, JPG, GIF, WEBP up to 30MB</span>
  </button>

  {#if picked.length > 0}
    <div class="previews">
      {#each picked as entry (entry.id)}
        <div class="preview">
          <img src={entry.preview} alt={entry.file.name} />
          <button
            type="button"
            class="delete"
            aria-label="Remove {entry.file.name}"
            onclick={() => remove(entry.id)}
          ></button>
          <span class="is-size-7 has-text-grey">{shortName(entry.file.name)}</span>
        </div>
      {/each}
    </div>
  {/if}
</div>

<input
  bind:this={input}
  type="file"
  {name}
  multiple
  accept={ACCEPT}
  onchange={handleChange}
  class="is-sr-only"
  data-testid="photo-input"
/>

<style>
  .dropzone {
    border: 2px dashed var(--bulma-border);
    border-radius: var(--bulma-radius-large);
    padding: 1rem;
    transition:
      border-color 150ms ease-out,
      background-color 150ms ease-out;
  }

  .dropzone.is-dragging {
    border-color: var(--bulma-link);
    background-color: var(--bulma-scheme-main-bis);
  }

  /* The whole empty area is the target, so the affordance is a full-width
     button rather than a click handler on the box itself. */
  .dropzone-open {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    padding: 1rem 0;
    border: 0;
    background: none;
    color: var(--bulma-text);
    cursor: pointer;
  }

  .previews {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .preview {
    position: relative;
    text-align: center;
  }

  .preview img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: var(--bulma-radius);
  }

  .preview .delete {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
  }
</style>
